import { db } from '../firebase';
import { 
  doc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { isQuotaError, isUnavailableOrOfflineError } from '../firebase';
import { getHolidays, getHolidayForDate } from './holidayService';
import { getCachedStudents, getCachedClasses, getCachedStaff, dataCache, getCachedQuery, getCachedDoc, getCachedCollection } from './dataCacheService';
import { getStudentClassForYear, isStudentEnrolledInYear, normalizeClassName, isSameClass } from './academicYearService';
import { Student } from '../types';

export interface DailyStats {
  totalStudents: number;
  presentToday: number;
  absentToday: number;
  staffAbsences?: number; // Legacy, keep for compatibility
  eduStaffAbsences: number;
  assistStaffAbsences: number;
  transportersArrived: number;
  totalTransporters: number;
  lastUpdated: any;
  classStats: Record<string, { total: number, present: number, className: string, color?: string, imageUrl?: string }>;
  isFallback?: boolean;
}

let statsQuotaCooldownUntil = 0;

/**
 * Recalculates and saves the daily stats for a given date and academic year.
 * This ensures exact accuracy between Student Management, Attendance, and Dashboard.
 */
export const refreshDailyStats = async (date: string, yearOrForce?: string | boolean, forceWrite: boolean = false) => {
  const effectiveYear = typeof yearOrForce === 'string' ? yearOrForce : '2026-2027';
  const effectiveForceWrite = typeof yearOrForce === 'boolean' ? yearOrForce : forceWrite;
  const cacheKey = `daily_stats_${date}_${effectiveYear}`;
  try {
    // OPTIMIZATION: Fetch cached active students for the specific academic year
    const [allStudents, allClasses, attendanceDocs, staffAbsenceDocs, transportDocs] = await Promise.all([
      getCachedStudents(effectiveForceWrite, effectiveYear),
      getCachedClasses(effectiveForceWrite),
      getCachedQuery(`attendance_${date}`, query(collection(db, 'studentAttendance'), where('date', '==', date)), effectiveForceWrite),
      getCachedQuery(`staff_absences_${date}`, query(collection(db, 'staffAbsences'), where('startDate', '<=', date)), effectiveForceWrite),
      getCachedQuery(`transport_${date}`, query(collection(db, 'transportAttendance'), where('date', '==', date)), effectiveForceWrite)
    ]);

    const attendanceMap: Record<string, any> = {};
    attendanceDocs.forEach((data: any) => {
      attendanceMap[data.studentId] = data;
    });

    const activeStaffAbsenceDocs = staffAbsenceDocs.filter((data: any) => {
      return data.endDate >= date;
    });

    // To ensure accuracy and avoid double-counting, we use staffId sets
    const eduStaffIds = new Set<string>();
    const assistStaffIds = new Set<string>();

    const allStaff = await getCachedStaff(effectiveForceWrite);
    const staffMap: Record<string, any> = {};
    allStaff.forEach(s => staffMap[s.id] = s);

    activeStaffAbsenceDocs.forEach((doc: any) => {
      const staffId = doc.staffId;
      if (!staffId) return;

      let effectiveType = doc.staffType;
      
      // Secondary check against master staff list for accurate categorization
      const staffMember = staffMap[staffId];
      if (staffMember) {
        const role = (staffMember.role || '').trim();
        const isAssistantRole = role.includes('סייע') || role.includes('אם בית') || role.includes('מלווה') || role.includes('נהג');
        
        effectiveType = staffMember.type;
        if (isAssistantRole) {
          effectiveType = 'assistant';
        } else if (!effectiveType) {
          effectiveType = 'educational';
        }
      }

      if (effectiveType === 'assistant') {
        assistStaffIds.add(staffId);
      } else {
        // Default to educational if not explicitly assistant
        eduStaffIds.add(staffId);
      }
    });

    const eduStaffAbsences = eduStaffIds.size;
    const assistStaffAbsences = assistStaffIds.size;

    const holidays = await getHolidays();
    const holiday = getHolidayForDate(date, holidays);

    // Fetch master transport lines list for total count
    const transportLines = await getCachedCollection('transportLines', effectiveForceWrite);
    const totalTransporters = transportLines.length;
    
    let transportersArrived = 0;
    if (transportDocs.length > 0) {
      const data = transportDocs[0] as any;
      transportersArrived = data.transporters?.filter((t: any) => t.arrived).length || 0;
    } else if (!holiday) {
      transportersArrived = totalTransporters;
    }

    let presentCount = 0;
    let absentCount = 0;
    const classStats: Record<string, { total: number; present: number; className: string; color?: string; imageUrl?: string }> = {};

    // 1. Initialize classStats for all existing classes in the school
    allClasses.forEach(cls => {
      classStats[cls.id] = { 
        total: 0, 
        present: 0, 
        className: cls.name,
        color: cls.color,
        imageUrl: cls.imageUrl
      };
    });

    // Helper to find the matching classStat entry for a student
    const resolveClassId = (rawId: string, rawName: string): string => {
      if (rawId && classStats[rawId]) return rawId;

      // Match by exact or normalized class name against allClasses
      const matched = allClasses.find(cls => 
        isSameClass({ id: rawId, name: rawName }, { id: cls.id, name: cls.name })
      );
      if (matched && classStats[matched.id]) {
        return matched.id;
      }

      // If student has a class name not yet in allClasses, create dynamic entry
      const fallbackId = rawId || rawName || 'unassigned';
      if (!classStats[fallbackId]) {
        classStats[fallbackId] = {
          total: 0,
          present: 0,
          className: rawName || rawId || 'ללא כיתה'
        };
      }
      return fallbackId;
    };

    // Filter students enrolled in the selected academic year and resolve their class for that year
    // CRITICAL: Strictly exclude inactive or unenrolled students
    const activeEnrolledStudents: { student: Student; targetClassId: string }[] = [];
    allStudents.forEach(student => {
      if (student.active === false || (student as any).isActive === false || (student as any).status === 'inactive') return;
      const classInfo = getStudentClassForYear(student, effectiveYear);
      if (classInfo && (classInfo.classId || classInfo.className)) {
        const targetClassId = resolveClassId(classInfo.classId, classInfo.className);
        activeEnrolledStudents.push({
          student,
          targetClassId
        });
      }
    });

    activeEnrolledStudents.forEach(({ student, targetClassId }) => {
      const att = attendanceMap[student.id];
      // Default to present if no record AND not a holiday
      const reallyPresent = att ? att.present : (holiday ? false : true);
      
      if (reallyPresent) presentCount++;
      else absentCount++;

      if (classStats[targetClassId]) {
        classStats[targetClassId].total++;
        if (reallyPresent) {
          classStats[targetClassId].present++;
        }
      }
    });

    const stats: DailyStats = {
      totalStudents: activeEnrolledStudents.length,
      presentToday: presentCount,
      absentToday: absentCount,
      eduStaffAbsences,
      assistStaffAbsences,
      staffAbsences: eduStaffAbsences + assistStaffAbsences,
      transportersArrived,
      totalTransporters,
      lastUpdated: new Date().toISOString(),
      classStats
    };

    // Save locally to cache immediately
    dataCache.set(cacheKey, stats, 5 * 60 * 1000); // 5 min cache
    dataCache.set(`daily_stats_${date}`, stats, 5 * 60 * 1000);
    dataCache.set(`doc_dailyStats_${date}_${effectiveYear}`, stats, 5 * 60 * 1000);
    dataCache.set(`doc_dailyStats_${date}`, stats, 5 * 60 * 1000);

    // Save to Firestore only if explicitly forceWrite AND quota is not exceeded
    const isQuotaFlagged = typeof window !== 'undefined' && (
      window.sessionStorage?.getItem('firestore_quota_exceeded') === 'true' ||
      window.localStorage?.getItem('firestore_quota_exceeded') === 'true'
    );
    if (effectiveForceWrite && !isQuotaFlagged && Date.now() > statsQuotaCooldownUntil) {
      try {
        const docId = `${date}_${effectiveYear}`;
        await setDoc(doc(db, 'dailyStats', docId), {
          ...stats,
          academicYear: effectiveYear,
          lastUpdated: serverTimestamp()
        });
      } catch (e) {
        if (isQuotaError(e)) {
          statsQuotaCooldownUntil = Date.now() + 60 * 60 * 1000;
          if (typeof window !== 'undefined') {
            window.sessionStorage?.setItem('firestore_quota_exceeded', 'true');
          }
          console.warn("Quota exceeded while saving daily stats to Firestore. Falling back to local cache.");
        }
      }
    }
    
    return stats;
  } catch (error: any) {
    const errMsg = error?.message || String(error);
    const isPermError = errMsg.includes('permission') || errMsg.includes('Missing or insufficient permissions');
    const isUnavailable = isUnavailableOrOfflineError(error);

    if (isQuotaError(error) || isPermError || isUnavailable) {
      console.warn("Quota, permission, or connection error while refreshing daily stats, using fallback or default:", error);
      const fallback = dataCache.get<DailyStats>(cacheKey, true) || dataCache.get<DailyStats>(`daily_stats_${date}`, true);
      if (fallback) return { ...fallback, isFallback: true };
      
      // Return stats with null values to indicate "missing data" rather than "zero"
      return {
        totalStudents: null as any,
        presentToday: null as any,
        absentToday: null as any,
        eduStaffAbsences: null as any,
        assistStaffAbsences: null as any,
        staffAbsences: null as any,
        transportersArrived: null as any,
        totalTransporters: null as any,
        lastUpdated: new Date().toISOString(),
        classStats: {},
        isFallback: true
      };
    }
    
    console.error("Error refreshing daily stats:", error);
    throw error;
  }
};

/**
 * Gets daily stats with caching and quota fallback.
 * Computes live from current enrolled active students to guarantee 100% synchronization
 * between the student management roster and the homepage attendance summary.
 */
export const getDailyStats = async (date: string, yearOrForce?: string | boolean, forceRefresh: boolean = false): Promise<DailyStats> => {
  const year = typeof yearOrForce === 'string' ? yearOrForce : '2026-2027';
  const isForce = typeof yearOrForce === 'boolean' ? yearOrForce : forceRefresh;
  const cacheKey = `daily_stats_${date}_${year}`;

  // 1. Try local session cache first if not forced
  if (!isForce) {
    const cached = dataCache.get<DailyStats>(cacheKey);
    if (cached) return cached;
  }

  // 2. Compute dynamic stats from current enrolled students and attendance records
  try {
    return await refreshDailyStats(date, year, isForce);
  } catch (error: any) {
    const errMsg = error?.message || String(error);
    const isPermError = errMsg.includes('permission') || errMsg.includes('Missing or insufficient permissions');
    const isUnavailable = isUnavailableOrOfflineError(error);

    if (isQuotaError(error) || isPermError || isUnavailable) {
      console.warn("Quota, permission, or connection error while getting daily stats, using fallback doc if available.");
      const fallback = dataCache.get<DailyStats>(cacheKey, true) || dataCache.get<DailyStats>(`daily_stats_${date}`, true);
      if (fallback) return { ...fallback, isFallback: true };

      try {
        const docFallback = await getCachedDoc<DailyStats>('dailyStats', `${date}_${year}`, false, 5 * 60 * 1000);
        if (docFallback) return { ...docFallback, isFallback: true };
      } catch {
        // ignore
      }
    }
    
    console.error("Error getting daily stats:", error);
    throw error;
  }
};

/**
 * Quickly updates a single student's attendance in the daily stats.
 * This avoids a full recalculation.
 */
let statsCache: Record<string, { data: DailyStats, timestamp: number }> = {};
const STATS_CACHE_TTL = 30 * 1000; // 30 seconds

export const updateStudentStat = async (date: string, classId: string, wasPresent: boolean | undefined, isPresent: boolean) => {
  try {
    const now = Date.now();
    const statsDoc = doc(db, 'dailyStats', date);
    
    let data: DailyStats | null = null;
    
    // Check local memory cache first to avoid repeated getDoc calls during rapid updates
    if (statsCache[date] && (now - statsCache[date].timestamp < STATS_CACHE_TTL)) {
      data = statsCache[date].data;
    } else {
      const snap = await getDoc(statsDoc);
      if (!snap.exists()) {
        // If stats don't exist for today, do a full refresh
        const refreshed = await refreshDailyStats(date);
        statsCache[date] = { data: refreshed, timestamp: now };
        return;
      }
      data = snap.data() as DailyStats;
      statsCache[date] = { data, timestamp: now };
    }
    
    // Calculate changes
    let presentChange = 0;
    let absentChange = 0;

    if (wasPresent === undefined) {
      // New record (default was absent in dashboard logic)
      if (isPresent) {
        presentChange = 1;
        absentChange = -1;
      }
    } else if (wasPresent !== isPresent) {
      if (isPresent) {
        presentChange = 1;
        absentChange = -1;
      } else {
        presentChange = -1;
        absentChange = 1;
      }
    }

    if (presentChange === 0 && absentChange === 0) return;

    // UPDATE: We remove the immediate setDoc call here to save write quota.
    // Instead, we rely on the manual/automatic refresh in the dashboard to update the global document.
    // We still update our local memory cache so this user sees the change reflected locally.
    
    // Update local memory cache to reflect changes
    if (statsCache[date]) {
      statsCache[date].data.presentToday += presentChange;
      statsCache[date].data.absentToday += absentChange;
      if (classId && statsCache[date].data.classStats[classId]) {
        statsCache[date].data.classStats[classId].present += presentChange;
      }
    }
    
    // Update dataCache for persistence across components
    const currentStats = dataCache.get<DailyStats>(`daily_stats_${date}`);
    if (currentStats) {
      currentStats.presentToday += presentChange;
      currentStats.absentToday += absentChange;
      if (classId && currentStats.classStats[classId]) {
        currentStats.classStats[classId].present += presentChange;
      }
      dataCache.set(`daily_stats_${date}`, currentStats);
    }
  } catch (error) {
    if (isQuotaError(error)) {
      console.warn("Quota exceeded while updating student stat (ignored)");
      return;
    }
    console.error("Error updating student stat:", error);
  }
};

/**
 * Clears cached daily stats so that subsequent calls recompute with fresh student/enrollment data.
 */
export const clearDailyStatsCache = (year?: string) => {
  statsCache = {};
  dataCache.removePrefix(`daily_stats_`);
  dataCache.removePrefix(`doc_dailyStats_`);
  dataCache.removePrefix(`stats_last_write_`);

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('daily_stats_cleared', { detail: { year } }));
  }
};

/**
 * Identifies students who have been absent for 3 or more consecutive school days.
 */
export const getLongTermAbsences = async (year?: string) => {
  const effectiveYear = year || '2026-2027';
  try {
    const allStudents = await getCachedStudents();
    const holidays = await getHolidays();
    
    // Filter students enrolled in the selected academic year and resolve their class for that year
    const enrolledStudents = allStudents
      .map(student => {
        const classInfo = getStudentClassForYear(student, effectiveYear);
        if (!classInfo || !classInfo.classId) return null;
        return {
          ...student,
          classId: classInfo.classId,
          className: classInfo.className
        };
      })
      .filter((s): s is Student => s !== null && s.active);

    // Get last 10 calendar days to find recent school days
    const schoolDays: string[] = [];
    let d = new Date();
    // Start from today or yesterday depending on time? 
    // Let's just take the last 10 calendar days and filter.
    for (let i = 0; i < 10; i++) {
      const dateStr = d.toISOString().split('T')[0];
      const day = d.getDay();
      const isWeekend = day === 5 || day === 6;
      const holiday = getHolidayForDate(dateStr, holidays);
      
      if (!isWeekend && !holiday) {
        schoolDays.push(dateStr);
      }
      d.setDate(d.getDate() - 1);
      if (schoolDays.length >= 5) break;
    }
    
    if (schoolDays.length < 3) return [];

    // Fetch attendance for these school days
    const attendanceResults = await Promise.all(
      schoolDays.map(date => 
        getCachedQuery(`attendance_${date}`, query(collection(db, 'studentAttendance'), where('date', '==', date)))
      )
    );
    
    // Map studentId -> { date -> present }
    const studentAttendanceMap: Record<string, Record<string, boolean>> = {};
    
    attendanceResults.forEach((docs, index) => {
      const date = schoolDays[index];
      docs.forEach((data: any) => {
        if (!studentAttendanceMap[data.studentId]) studentAttendanceMap[data.studentId] = {};
        studentAttendanceMap[data.studentId][date] = data.present;
      });
    });
    
    const longTermAbsentees = enrolledStudents.filter(student => {
      let consecutiveAbsences = 0;
      for (const date of schoolDays) {
        const att = studentAttendanceMap[student.id]?.[date];
        // In this system, if no record exists, the student is considered present by default
        // unless it's a holiday (which we already filtered out).
        const isPresent = att !== undefined ? att : true; 
        
        if (!isPresent) {
          consecutiveAbsences++;
        } else {
          // Presence breaks the streak
          break;
        }
      }
      return consecutiveAbsences >= 3;
    }).map(s => {
      // Calculate exact consecutive days
      let count = 0;
      for (const date of schoolDays) {
        if (studentAttendanceMap[s.id]?.[date] === false) count++;
        else break;
      }
      return {
        id: s.id,
        fullName: s.fullName,
        className: s.className,
        classId: s.classId,
        consecutiveDays: count
      };
    });
    
    return longTermAbsentees;
  } catch (error) {
    console.error("Error calculating long term absences:", error);
    return [];
  }
};
